/* =========================================================
   «Te faltan 13» deja de ser un dato muerto.

   El contador de pendientes pasa a ser el botón que abre la lista. Cerrado
   no ocupa nada; abierto enseña cada campo por su nombre y dónde está.

   Dos formas de la misma cosa:

     Sede          los campos de la ficha y los de sus instructores.
     Organización  lo suyo, cada sede PLEGADA —un socio con cuatro puede
                   tener treinta y tantos campos ahí dentro, y
                   desplegarlos todos convertiría la lista en otro
                   formulario— y los documentos de cumplimiento, que hoy
                   cuentan para el porcentaje y no salen en ninguna parte.

   Al llegar al 100 % el disparador se vuelve verde y deja de abrir nada:
   en su lugar sale un aviso que recuerda que se puede seguir editando.
   ========================================================= */
import React, { useState } from 'react';
import { ic } from '@/components/Icons';
import { useI18n } from '@/i18n';
import { pendientesDeOrg, pendientesDeSite } from '@/lib/pendientes';
import { VistoBueno } from './VistoBueno';
import type { Falta, FaltasDeInstructor, FaltasDeOrg, FaltasDeSite } from '@/lib/pendientes';

/* ---------------- piezas ---------------- */

function Linea({ falta }: { falta: Falta }) {
  const { t } = useI18n();
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        background: 'var(--surface-2)', border: '1px solid var(--line)',
        borderLeft: `3px solid ${falta.obligatorio ? 'var(--st-req)' : 'var(--st-edit)'}`,
        borderRadius: 7, padding: '8px 11px',
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0 }}>{falta.etiqueta}</span>
      {falta.obligatorio ? (
        <span className="chip err" style={{ padding: '1px 7px', fontSize: 10 }}>
          {t('field.state_required')}
        </span>
      ) : (
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('sec.' + falta.seccion)}</span>
      )}
    </div>
  );
}

const Rejilla = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 7 }}>
    {children}
  </div>
);

function Grupo({ titulo, cuenta, children }: { titulo: string; cuenta: number; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 15 }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9,
          fontSize: 12, fontWeight: 800, letterSpacing: '.04em',
          textTransform: 'uppercase', color: 'var(--text-2)',
        }}
      >
        {titulo}
        <span
          style={{
            background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 99,
            padding: '1px 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-2)',
          }}
        >
          {cuenta}
        </span>
      </div>
      {children}
    </div>
  );
}

function PorInstructor({ gente }: { gente: FaltasDeInstructor[] }) {
  return (
    <>
      {gente.map((i) => (
        <div key={i.id} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, margin: '0 0 6px' }}>
            {i.nombre} <span className="mono" style={{ fontWeight: 500, color: 'var(--text-3)', fontSize: 11 }}>· {i.id}</span>
          </div>
          <Rejilla>{i.faltas.map((f) => <Linea key={f.campo} falta={f} />)}</Rejilla>
        </div>
      ))}
    </>
  );
}

/** Una sede dentro de la organización: plegada por defecto. */
function SedePlegable({ sede }: { sede: FaltasDeSite }) {
  const { lang } = useI18n();
  const [abierta, setAbierta] = useState(false);
  const enInstructores = sede.total - sede.propias.length;
  return (
    <div style={{ marginBottom: 6 }}>
      <button
        className="btn"
        onClick={() => setAbierta((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 12px', justifyContent: 'flex-start', fontWeight: 500,
        }}
      >
        <span style={{ color: 'var(--text-3)', fontSize: 11, width: 10 }}>{abierta ? '▾' : '›'}</span>
        <span style={{ fontSize: 13, fontWeight: 700, flex: 1, textAlign: 'left' }}>{sede.nombre}</span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{sede.id}</span>
        <span className="chip err" style={{ padding: '1px 7px', fontSize: 10 }}>{sede.total}</span>
      </button>
      {abierta ? (
        <div style={{ padding: '8px 0 10px 22px' }}>
          {sede.propias.length ? (
            <Rejilla>{sede.propias.map((f) => <Linea key={f.campo} falta={f} />)}</Rejilla>
          ) : null}
          {enInstructores > 0 ? (
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: sede.propias.length ? 7 : 0 }}>
              {lang === 'es'
                ? `…y ${enInstructores} en los instructores de esta sede`
                : `…and ${enInstructores} in this site's instructors`}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ---------------- el disparador ---------------- */

/** El contador, que ahora abre la lista. Verde y quieto cuando no falta nada. */
export function DisparadorPendientes({
  faltan, abierto, onToggle,
}: { faltan: number; abierto: boolean; onToggle: () => void }) {
  const { lang } = useI18n();
  if (faltan === 0) {
    return (
      <span
        className="chip ok"
        style={{ padding: '3px 11px', fontSize: 12, fontWeight: 700, display: 'inline-flex', gap: 6 }}
      >
        ✓ {lang === 'es' ? 'Sin pendientes' : 'Nothing pending'}
      </span>
    );
  }
  return (
    <button
      onClick={onToggle}
      className="chip err"
      style={{
        padding: '3px 11px', fontSize: 12, fontWeight: 700, border: '1px solid var(--st-req)',
        display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
      }}
      aria-expanded={abierto}
    >
      {faltan} {lang === 'es' ? 'pendientes' : 'pending'}
      <span style={{ fontSize: 10 }}>{abierto ? '▴' : '▾'}</span>
    </button>
  );
}

/* ---------------- el aviso de que ya no falta nada ---------------- */

/** Lo que ve el socio al llegar al 100 %.
 *
 *  Es una franja, no una ventana: pedirle que revise TODO y a la vez que
 *  conteste ahora, en algo que le tapa la pantalla, solo conseguiría que
 *  pulse para quitárselo de encima. */
export function AvisoCompleto({ resumen }: { resumen?: string }) {
  const { lang } = useI18n();
  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px',
        borderTop: '1px solid var(--line)', background: 'var(--st-read-l)',
      }}
    >
      <span
        style={{
          width: 26, height: 26, borderRadius: 99, background: 'var(--ok)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 800, flexShrink: 0,
        }}
        aria-hidden="true"
      >
        ✓
      </span>
      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.55, color: '#14543E' }}>
        <b style={{ color: '#0C3A2B' }}>
          {lang === 'es' ? 'Ya no te faltan campos.' : 'Nothing left to fill in.'}
        </b>{' '}
        {lang === 'es'
          ? 'Te recomendamos revisar todas tus fichas para asegurarte de que nada quedó mal escrito o desactualizado. Puedes seguir modificando tus datos en cualquier momento hasta que el proceso cierre.'
          : 'We recommend reviewing every record to make sure nothing is mistyped or out of date. You can keep editing until the process closes.'}
        {resumen ? <div style={{ marginTop: 5, fontSize: 12, opacity: 0.85 }}>{resumen}</div> : null}
      </div>
    </div>
  );
}

/* ---------------- los dos paneles ---------------- */

const Panel = ({ children }: { children: React.ReactNode }) => (
  <div style={{ borderTop: '1px solid var(--line)', padding: '16px 18px 18px' }}>{children}</div>
);

const Nota = ({ texto }: { texto: string }) => (
  <div
    style={{
      marginTop: 14, padding: '10px 12px', background: 'var(--surface-2)',
      borderLeft: '3px solid var(--st-read)', borderRadius: 6,
      fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5,
    }}
  >
    {texto}
  </div>
);

export function PendientesDeSite({ datos }: { datos: FaltasDeSite }) {
  const { lang } = useI18n();
  return (
    <Panel>
      {datos.propias.length ? (
        <Grupo titulo={lang === 'es' ? 'En esta sede' : 'In this site'} cuenta={datos.propias.length}>
          <Rejilla>{datos.propias.map((f) => <Linea key={f.campo} falta={f} />)}</Rejilla>
        </Grupo>
      ) : null}
      {datos.instructores.length ? (
        <Grupo
          titulo={lang === 'es' ? 'En tus instructores' : "In your instructors"}
          cuenta={datos.instructores.reduce((n, i) => n + i.faltas.length, 0)}
        >
          <PorInstructor gente={datos.instructores} />
        </Grupo>
      ) : null}
      <Nota
        texto={lang === 'es'
          ? 'Un campo que marques «No aplica» desaparece de esta lista y deja de contar.'
          : 'A field you mark as "N/A" leaves this list and stops counting.'}
      />
    </Panel>
  );
}

export function PendientesDeOrg({ datos }: { datos: FaltasDeOrg }) {
  const { lang } = useI18n();
  return (
    <Panel>
      {datos.propias.length ? (
        <Grupo titulo={lang === 'es' ? 'En la organización' : 'In the organization'} cuenta={datos.propias.length}>
          <Rejilla>{datos.propias.map((f) => <Linea key={f.campo} falta={f} />)}</Rejilla>
        </Grupo>
      ) : null}
      {datos.sedes.length ? (
        <Grupo
          titulo={lang === 'es' ? 'En tus sedes' : 'In your sites'}
          cuenta={datos.sedes.reduce((n, s) => n + s.total, 0)}
        >
          {datos.sedes.map((s) => <SedePlegable key={s.id} sede={s} />)}
        </Grupo>
      ) : null}
      {datos.documentos.length ? (
        <Grupo titulo={lang === 'es' ? 'Documentos por leer' : 'Documents to read'} cuenta={datos.documentos.length}>
          <Rejilla>
            {datos.documentos.map((d) => (
              <div
                key={d.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 9, background: 'var(--surface-2)',
                  border: '1px solid var(--line)', borderLeft: '3px solid var(--st-read)',
                  borderRadius: 7, padding: '8px 11px',
                }}
              >
                <span style={{ color: 'var(--st-read)', display: 'inline-flex' }}>{ic.flag}</span>
                <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0 }}>{d.titulo}</span>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {d.pct} % {lang === 'es' ? 'leído' : 'read'}
                </span>
              </div>
            ))}
          </Rejilla>
        </Grupo>
      ) : null}
      <Nota
        texto={lang === 'es'
          ? 'Un campo marcado «No aplica» desaparece de esta lista y deja de contar. Los documentos se dan por cumplidos cuando alguien de tu organización los lee enteros.'
          : 'A field marked "N/A" leaves this list and stops counting. Documents count as met once someone from your organization reads them in full.'}
      />
    </Panel>
  );
}

/* ---------------- lo que se usa desde las pantallas ----------------

   Una tarjeta entera y autónoma. Se coloca debajo de la cabecera de la
   ficha y no depende de cómo esté montada esa cabecera, que es distinta
   en cada una de las tres variantes. Una línea por variante en lugar de
   tres cirugías.

   Resuelve la lista por su cuenta: la pantalla solo dice de qué ficha y
   le pasa el borrador, para que la lista baje mientras el socio escribe.  */

/** @param pie lo que va al fondo de la tarjeta, debajo del aviso o de la
 *              lista. Es el visto bueno, y solo lo pone la variante de
 *              organización: una sede suelta no se declara terminada. */
function Caja({ faltan, panel, pie }: { faltan: number; panel: React.ReactNode; pie?: React.ReactNode }) {
  const { lang } = useI18n();
  const [abierto, setAbierto] = useState(false);
  if (faltan === 0) {
    return (
      <div className="card" style={{ overflow: 'hidden' }}>
        <AvisoCompleto />
        {pie}
      </div>
    );
  }
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
          {lang === 'es' ? 'Lo que te falta' : "What's missing"}
        </span>
        <DisparadorPendientes faltan={faltan} abierto={abierto} onToggle={() => setAbierto((v) => !v)} />
      </div>
      {abierto ? panel : null}
      {pie}
    </div>
  );
}

export function CajaPendientesSite({
  siteId, borrador,
}: { siteId: string; borrador?: Record<string, unknown> | null }) {
  const { lang } = useI18n();
  const datos = pendientesDeSite(siteId, lang, borrador);
  if (!datos) return null;
  return <Caja faltan={datos.total} panel={<PendientesDeSite datos={datos} />} />;
}

export function CajaPendientesOrg({
  orgId, borrador,
}: { orgId: string; borrador?: Record<string, unknown> | null }) {
  const { lang } = useI18n();
  const datos = pendientesDeOrg(orgId, lang, borrador);
  /* La misma cuenta SIN el borrador: lo que hay guardado ahora mismo.
     El visto bueno habla de lo guardado, no de lo que se está
     escribiendo. Con el borrador, vaciar un campo ponía el aviso de
     «tu ficha dejó de estar completa» antes de darle a Guardar —y si
     luego se cancelaba, el aviso se había dado por nada—. Es además la
     señal correcta para volver a preguntarle al servidor: cambia al
     guardar, que es cuando el servidor tiene algo nuevo que decir. */
  const guardados = pendientesDeOrg(orgId, lang);
  if (!datos) return null;
  return (
    <Caja
      faltan={datos.total}
      panel={<PendientesDeOrg datos={datos} />}
      pie={<VistoBueno orgId={orgId} faltan={guardados?.total ?? datos.total} />}
    />
  );
}
